import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { SensorType } from '../../types/sensor-type';
import _ from 'lodash';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzInfoBoxWebComponent, GzTitleWebComponent } from '../../../../../../webcomponents/src/index.ui';

export class AzureADSensorView extends BaseSensorWebView {
  public formGroup: FormGroup;

  public dom: {
    $applicationID: GzFormFieldWebComponent;
    $tenantID: GzFormFieldWebComponent;
    $clientSecret: GzFormFieldWebComponent;
    $connectionString: GzFormFieldWebComponent;
    $eventHubName: GzFormFieldWebComponent;
    $consumerGroup: GzFormFieldWebComponent;
    $eventHubCheckbox: GzCheckboxWebComponent;
    $azureEventHubCredentialsTitle: GzTitleWebComponent;
    $entraIDAppCredentialsTitle: GzTitleWebComponent;
    $eventHubInfoBox: GzInfoBoxWebComponent;
    $entraIDSubtitle: GzTitleWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.AZURE_AD_AUDIT}">
          <gz-title class="sensor-title">${dependencies.translateFn(
            'Before you integrate Microsoft Entra ID with GravityZone XDR, make sure the following requirements are met:'
          )}
          </gz-title>
          <ol type="1">
            <li>${dependencies.translateFn('You have registered an application in the {registerLink}.', {
              registerLink:
                '<gz-link type="underline" href="https://entra.microsoft.com/"><span>' +
                dependencies.translateFn('Microsoft Entra admin center') +
                '</span></gz-link>',
            })}</li>
            <li>${dependencies.translateFn(
              'You have added to your application the following Application permissions to access Microsoft Graph:'
            )}
                <ul style="list-style-type: lower-alpha !important;">
                  <li>
                    <gz-text class="d-inline" fontSize="14" fontWeight="600">AuditLog.Read.All, Directory.Read.All</gz-text>
                  </li>
                  <li>${dependencies.translateFn('Optional: {mailPermission} for deleting emails', {
                    mailPermission: '<gz-text class="d-inline" fontSize="14" fontWeight="600">Mail.ReadWrite</gz-text>',
                  })}</li>
                  <li>${dependencies.translateFn(
                    'Optional: {userReadWrite} {userEnableDisable} and {userRevoke} for enforcing password resets and accounts disabling directly from GravityZone Control Center',
                    {
                      userReadWrite:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">User.ReadWrite.All,</gz-text>',
                      userEnableDisable:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">User.EnableDisableAccount.All,</gz-text>',
                      userRevoke:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">User.RevokeSessions.All</gz-text>',
                    }
                  )}</li>
                  <li>${dependencies.translateFn(
                    'Optional: {identityRiskyUserReadWriteAll} for marking a Microsoft Entra ID account as compromised',
                    {
                      identityRiskyUserReadWriteAll:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">IdentityRiskyUser.ReadWrite.All</gz-text>',
                    }
                  )}</li>
                  <li>${dependencies.translateFn(
                    'Optional: {identityRiskyUserReadAll} for displaying Microsoft Entra ID risky user information in the XDR incident graph details panel',
                    {
                      identityRiskyUserReadAll:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">IdentityRiskyUser.Read.All</gz-text>',
                    }
                  )}</li>
                </ul>
            </li>
            <p>${dependencies.translateFn(
              'Note: {identityRiskyUserReadWrite} and {identityRiskyUserRead} require a Microsoft Entra ID P2 license. All the other permissions require a Microsoft Entra ID P1 license.',
              {
                identityRiskyUserReadWrite:
                  '<gz-text class="d-inline" fontSize="14" fontWeight="600">IdentityRiskyUser.ReadWrite.All</gz-text>',
                identityRiskyUserRead:
                  '<gz-text class="d-inline" fontSize="14" fontWeight="600">IdentityRiskyUser.Read.All</gz-text>',
              }
            )}</p>
            <li>${dependencies.translateFn(
              'You have granted Admin consent to the assigned Application permissions.'
            )}</li>
            <li>${dependencies.translateFn(
              'Optional: you have assigned the {userAdministrator} role to your application. It is required only to enforce password resets on Microsoft 365 accounts directly from GravityZone.',
              {
                userAdministrator:
                  '<gz-text class="d-inline" fontSize="14" fontWeight="600">User Administrator</gz-text>',
              }
            )}</li>
            <li>${dependencies.translateFn(
              'Optional: you have assigned the {globalAdministrator} role to your application. It is required only to take response actions on administrator users.',
              {
                globalAdministrator:
                  '<gz-text class="d-inline" fontSize="14" fontWeight="600">Global Administrator</gz-text>',
              }
            )}</li>
            <li>${dependencies.translateFn('You have generated a client secret for your application.')}</li>
             <li>${dependencies.translateFn(
               'Optional, if you want to use Azure Event Hubs for security events retrieval:'
             )}
                <ul style="list-style-type: lower-alpha !important;">
                  <li>
                  ${dependencies.translateFn('You have created and configured an event hub in {azurePortalLink}.', {
                    azurePortalLink:
                      '<gz-link type="underline" href="https://portal.azure.com/"><span>' +
                      dependencies.translateFn('the Azure Portal') +
                      '</span></gz-link>',
                  })}
                  </li>
                  <li>${dependencies.translateFn('You have obtained a connection string for the event hub.')}</li>
                  <li>${dependencies.translateFn(
                    'You have enabled log routing from the Microsoft Entra admin center to your event hub. The selected log categories are: {auditLogs}{signInLogs}{provisioningLogs}and {riskyUsers}.',
                    {
                      auditLogs: '<gz-text class="d-inline" fontSize="14" fontWeight="600">AuditLogs,</gz-text>',
                      signInLogs: '<gz-text class="d-inline" fontSize="14" fontWeight="600">SignInLogs,</gz-text>',
                      provisioningLogs:
                        '<gz-text class="d-inline" fontSize="14" fontWeight="600">ProvisioningLogs,</gz-text>',
                      riskyUsers: '<gz-text class="d-inline" fontSize="14" fontWeight="600">RiskyUsers</gz-text>',
                    }
                  )}
                </ul>
            </li>
          </ol>
          <p>${dependencies.translateFn('For detailed instructions, refer to {detailLink}.', {
            detailLink:
              '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77209-452346-the-azure-ad-sensor.html"><span>' +
              dependencies.translateFn('The Azure AD sensor') +
              '</span></gz-link>',
          })}</p>
        </div>
        `;
  }

  protected createFormGroup() {
    this.dom.$applicationID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ApplicationId.MaxLength,
        this.dependencies.translateFn('The application ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ApplicationId.MaxLength,
        })
      ),
    ];
    this.dom.$tenantID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.TenantId.MaxLength,
        this.dependencies.translateFn('The tenant ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.TenantId.MaxLength,
        })
      ),
    ];

    this.dom.$clientSecret.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The Client Secret must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ClientSecret.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Client Secret cannot contain spaces')),
    ];
    this.dom.$connectionString.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Connection string cannot contain spaces')),
    ];
    this.dom.$eventHubName.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.HubName.MaxLength,
        this.dependencies.translateFn('The Connection string must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.HubName.MaxLength,
        })
      ),
    ];
    this.dom.$consumerGroup.Validators = [
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The Consumer group must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ConsumerGroup.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Consumer Group cannot contain spaces')),
    ];

    this.formGroup = new FormGroup({
      application_id: this.dom.$applicationID,
      tenant_id: this.dom.$tenantID,
      client_secret: this.dom.$clientSecret,
      connection_string: this.dom.$connectionString,
      event_hub_name: this.dom.$eventHubName,
      consumer_group: this.dom.$consumerGroup,
    });
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $applicationID: this.shadow.querySelector('#azure_ad_application_id'),
      $tenantID: this.shadow.querySelector('#azure_ad_tenant_id'),
      $clientSecret: this.shadow.querySelector('#azure_ad_client_secret'),
      $connectionString: this.shadow.querySelector('#azure_ad_connection_string'),
      $eventHubName: this.shadow.querySelector('#azure_ad_hub_name'),
      $consumerGroup: this.shadow.querySelector('#azure_ad_consumer_group'),
      $eventHubCheckbox: this.shadow.querySelector('#event-hub-checkbox'),
      $entraIDAppCredentialsTitle: this.shadow.querySelector('#entra-id-app-credentials-id-title'),
      $azureEventHubCredentialsTitle: this.shadow.querySelector('#azure-event-hub-credentials-title'),
      $eventHubInfoBox: this.shadow.querySelector('#event-hub-info-box'),
      $entraIDSubtitle: this.shadow.querySelector('#entra-id-subtitle'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  setTranslatedStrings(): void {
    this.dom.$applicationID.label = this.dependencies.translateFn('Application ID');
    this.dom.$tenantID.label = this.dependencies.translateFn('Tenant ID');
    this.dom.$clientSecret.label = this.dependencies.translateFn('Client secret');
    this.dom.$connectionString.label = this.dependencies.translateFn('Event hub connection string');
    this.dom.$eventHubName.label = this.dependencies.translateFn('Event hub name');
    this.dom.$consumerGroup.label = this.dependencies.translateFn('Consumer group');
    this.dom.$eventHubCheckbox.label = this.dependencies.translateFn(
      'Do not use Azure Event Hubs for security events retrieval'
    );
    this.dom.$azureEventHubCredentialsTitle.innerText = this.dependencies.translateFn('Azure Event Hub Credentials');
    this.dom.$entraIDAppCredentialsTitle.innerText = this.dependencies.translateFn(
      'Microsoft Entra ID application credentials'
    );
    this.dom.$eventHubInfoBox.innerText = this.dependencies.translateFn(
      'Using Azure Event Hubs is recommended. Using only Entra ID app integration may result in delays in security events retrieval from Microsoft Entra ID.'
    );
    this.dom.$entraIDSubtitle.innerText = this.dependencies.translateFn(
      'This integration relies on Microsoft Graph API.'
    );
    this.setBaseTranslatedStrings(SensorType.AZURE_AD_AUDIT);
  }

  modifyFieldValidators(eventHubFieldsEnabled: boolean): void {
    const requiredValidator = RequiredValidator(this.commonStrings.requiredField);

    if (eventHubFieldsEnabled) {
      this.dom.$eventHubName.Validators = [
        MaxLengthValidator(
          EditIntegrationConstants.HubName.MaxLength,
          this.dependencies.translateFn("The Event hub name must have a maximum of {maxLength} characters.", {
            maxLength: EditIntegrationConstants.HubName.MaxLength,
          })
        ),
      ];

      this.dom.$connectionString.Validators = [
        RegexValidator(/^\S*$/, this.dependencies.translateFn('The Connection string cannot contain spaces')),
      ];
    } else {
      this.dom.$eventHubName.Validators = [
        requiredValidator,
        MaxLengthValidator(
          EditIntegrationConstants.HubName.MaxLength,
          this.dependencies.translateFn("The Event hub name must have a maximum of {maxLength} characters.", {
            maxLength: EditIntegrationConstants.HubName.MaxLength,
          })
        ),
      ];

      this.dom.$connectionString.Validators = [
        requiredValidator,
        RegexValidator(/^\S*$/, this.dependencies.translateFn('The Connection String cannot contain spaces')),
      ];
    }
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    // If form is being edited, map values to form fields
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        application_id: _.get(viewData, ['response', 'application_id'], ''),
        tenant_id: _.get(viewData, ['response', 'tenant_id'], ''),
        client_secret: _.get(viewData, ['response', 'client_secret'], ''),
        connection_string: _.get(viewData, ['response', 'connection_string'], ''),
        event_hub_name: _.get(viewData, ['response', 'event_hub_name'], ''),
        consumer_group: _.get(viewData, ['response', 'consumer_group'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
